import { useSelector } from "react-redux";
import { useCallback, useState } from "react";

import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import {
  CircularProgress,
  Checkbox,
  FormControlLabel,
  TextField,
} from "@mui/material";

import useDebounce from "app/hooks/useDebounce";
import { findPatientRecordsByName } from "app/services/patient/patient";
import { getClinicUserFacility } from "utils/currentUser.util";

const SearchPatient = ({
  handlePatientSelect,
  isInofficeClinicUpload = () => false,
  account,
  hideSearchByDeviceSerialNumber = false,
  label = "Search Existing Patients",
  style,
  required = false,
  freeSolo,
  disabled = false,
}) => {
  const [patientList, setPatientList] = useState([]);
  const [isPatientListOpen, setIsPatientListOpen] = useState(false);
  const [isPatientListFetching, setIsPatientListFetching] = useState(false);
  const [searchByDeviceSerialNumber, setSearchByDeviceSerialNumber] =
    useState(false);

  const filter = createFilterOptions();

  const { selectedPatient } = useSelector((state) => state.manualUpload);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedDispatch = useCallback(
    useDebounce(
      async (value, searchByDeviceSerialNumber, facility, account) => {
        setIsPatientListFetching(true);
        const patientRecords = await findPatientRecordsByName(
          value,
          searchByDeviceSerialNumber,
          facility,
          account
        );
        if (patientRecords) {
          setPatientList(patientRecords.patients);
        }
        setIsPatientListFetching(false);
        setIsPatientListOpen(true);
      },
      750
    ),
    []
  );

  const handleSearchExistingPatient = async (
    searchValue,
    searchByDeviceSerialNumber
  ) => {
    const facility = isInofficeClinicUpload() ? getClinicUserFacility() : "";
    if (searchValue) {
      debouncedDispatch(
        searchValue,
        searchByDeviceSerialNumber,
        facility,
        account
      );
    }
  };

  return (
    <>
      <Autocomplete
        fullWidth
        open={isPatientListOpen}
        onClose={() => {
          setIsPatientListOpen(false);
        }}
        freeSolo={
          freeSolo ?? !(patientList.length === 0 && !isPatientListFetching)
        }
        filterSelectedOptions
        selectOnFocus
        handleHomeEndKeys
        id="free-solo-with-text-demo"
        disableClearable={true}
        disabled={disabled}
        ListboxProps={{ style: { maxHeight: 240 } }}
        style={style || { marginTop: "20px", marginBottom: "8px" }}
        noOptionsText={"No patients found."}
        value={selectedPatient?.name}
        options={patientList.length ? patientList : []}
        renderOption={(props, option) => {
          const { key, ...optionProps } = props;
          const {
            dob = "",
            deviceInformation = {},
            account,
            evoke_id,
          } = option;
          const { modelNumber = "" } = deviceInformation;

          return (
            <div
              key={key}
              style={{
                padding: "5px 10px",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-start",
                alignItems: "flex-start",
              }}
              data-action="select-patient-option"
              {...optionProps}
            >
              <div>{option.name}</div>
              <div style={{ fontSize: 12, color: "#757575" }}>
                DOB: {dob} | Evoke ID: {evoke_id} | Serial Number: {modelNumber}{" "}
                | Account: {account}
              </div>
            </div>
          );
        }}
        onChange={(event, selectedValue) => {
          handlePatientSelect(selectedValue);
        }}
        onInputChange={(event, newValue, reason) => {
          if (reason === "input") {
            handleSearchExistingPatient(newValue, searchByDeviceSerialNumber);
          }
        }}
        filterOptions={(options, params) => {
          const filtered = filter(options, params);
          return filtered.length ? filtered : options;
        }}
        getOptionLabel={(option) => {
          return option?.name || option;
        }}
        renderInput={(params) => (
          <div style={{ position: "relative" }}>
            <TextField
              {...params}
              required={required}
              label={label}
              data-text="search-existing-patients-field"
            />

            {isPatientListFetching && (
              <CircularProgress
                size={20}
                style={{
                  position: "absolute",
                  top: "40%",
                  right: 44,
                  color: "#94BA33",
                }}
              />
            )}
          </div>
        )}
      />
      {!hideSearchByDeviceSerialNumber && (
        <div
          style={{
            display: "flex",
            justifyContent: "end",
            marginBottom: "5px",
          }}
        >
          <FormControlLabel
            control={
              <Checkbox
                checked={searchByDeviceSerialNumber}
                size="small"
                onChange={(e, c) => {
                  setSearchByDeviceSerialNumber(c);
                }}
                data-action="search-by-device-serial-number-checkbox"
              />
            }
            label={
              <span style={{ fontSize: "14px", color: "#00000099" }}>
                Search by Device Serial Number
              </span>
            }
          />
        </div>
      )}
    </>
  );
};

export default SearchPatient;
